import React, { useState, useEffect, useRef } from 'react';
import { getBacktestPlayer, PlaybackSpeed, PlaybackState } from '../services/BacktestPlayer';
import { useDraggable } from '../hooks/useDraggable';
import './BacktestControls.css';

interface BacktestControlsProps {
  onClose: () => void;
}

const BacktestControls: React.FC<BacktestControlsProps> = ({ onClose }) => {
  const [playbackState, setPlaybackState] = useState<PlaybackState>('stopped');
  const [speed, setSpeed] = useState<PlaybackSpeed>(500);
  const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [currentTime, setCurrentTime] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [hoverTime, setHoverTime] = useState<Date | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);

  const controlsRef = useRef<HTMLDivElement>(null);

  // Use draggable hook
  const { position, isDragging, dragHandlers } = useDraggable(controlsRef, {
    defaultPosition: { x: 20, y: window.innerHeight - 150 },
    preferenceKey: 'backtestControls',
    constrainToViewport: true,
    excludeSelectors: ['button', 'input'],
  });

  // Seekbar scrubbing state
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [, setPendingSeekIndex] = useState<number | null>(null);
  const seekTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const player = getBacktestPlayer({
    onProgress: (current, total, timestamp) => {
      setProgress({
        current,
        total,
        percentage: (current / total) * 100
      });
      setCurrentTime(new Date(timestamp));
    },
    onStateChange: (state) => {
      setPlaybackState(state);
    },
    onComplete: () => {
      setPlaybackState('completed');
    }
  });

  useEffect(() => {
    // Initialize state from player
    const currentProgress = player.getProgress();
    setProgress(currentProgress);
    setPlaybackState(player.getState());

    const timestamp = player.getCurrentTimestamp();
    if (timestamp) {
      setCurrentTime(new Date(timestamp));
    }

    // Get start and end times
    const start = player.getStartTimestamp();
    const end = player.getEndTimestamp();
    if (start) setStartTime(new Date(start));
    if (end) setEndTime(new Date(end));

    // Set initial playback speed to 500x
    player.setSpeed(500);

    // Cleanup timeout on unmount
    return () => {
      if (seekTimeoutRef.current) {
        clearTimeout(seekTimeoutRef.current);
      }
    };
  }, []);

  const handlePlayPause = () => {
    if (playbackState === 'playing') {
      player.pause();
    } else {
      player.play();
    }
  };

  const handleSpeedChange = (newSpeed: PlaybackSpeed) => {
    setSpeed(newSpeed);
    player.setSpeed(newSpeed);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(e.target.value, 10);

    // Auto-pause if playing when user starts seeking
    if (playbackState === 'playing' && !isScrubbing) {
      player.pause();
    }

    // Mark as scrubbing and store the pending index
    setIsScrubbing(true);
    setPendingSeekIndex(index);

    // Update progress immediately for visual feedback
    setProgress(prev => ({
      ...prev,
      current: index,
      percentage: (index / prev.total) * 100
    }));

    // Clear any existing timeout
    if (seekTimeoutRef.current) {
      clearTimeout(seekTimeoutRef.current);
    }

    // Debounce the actual seek operation (only fire after user stops moving for 150ms)
    seekTimeoutRef.current = setTimeout(() => {
      player.seekToIndex(index);
      setIsScrubbing(false);
      setPendingSeekIndex(null);
    }, 150);
  };

  const handleSliderHover = (e: React.MouseEvent<HTMLInputElement>) => {
    // Don't update hover tooltip while scrubbing (to avoid confusion)
    if (isScrubbing) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const hoveredIndex = Math.floor(percentage * (progress.total - 1));

    if (hoveredIndex >= 0 && hoveredIndex < progress.total) {
      const timestamp = player.getTimestampAtIndex(hoveredIndex);
      if (timestamp) {
        const hoverDate = new Date(timestamp);
        setHoverTime(hoverDate);
        // Use relative X position within the slider
        setTooltipPosition({ x, y: 0 });
      } else {
        console.warn('❌ No timestamp for index:', hoveredIndex);
      }
    }
  };

  const handleSliderLeave = () => {
    setHoverTime(null);
    setTooltipPosition(null);
  };

  const handleStepForward = () => {
    player.stepForward();
  };

  const handleStepBackward = () => {
    // Fire and forget - don't await in event handler
    player.stepBackward();
  };

  const formatTime = (date: Date | null): string => {
    if (!date) return '--:--:--';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return '----/--/--';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const speeds: PlaybackSpeed[] = [1, 2, 5, 10, 50, 100, 500, 1000];

  return (
    <div
      ref={controlsRef}
      className={`backtest-controls ${isDragging ? 'dragging' : ''}`}
      style={{
        bottom: `${position.y}px`,
        left: `${position.x}px`,
        transform: 'none'
      }}
    >
      <div className="backtest-header" {...dragHandlers}>
        <div className="header-content">
          <h3>Backtest</h3>
          <div className="time-range-display">
            <div className="date-range">
              {startTime && endTime && (
                <span className="range-text">
                  {formatDate(startTime)} - {formatDate(endTime)}
                </span>
              )}
            </div>
            <span className="time-display">{formatTime(currentTime)}</span>
          </div>
        </div>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      <div className="controls-row">
        <div className="playback-controls">
          <button
            className="control-btn"
            onClick={handleStepBackward}
            disabled={playbackState === 'playing' || progress.current === 0}
            title="Step Backward"
          >
            ⏮
          </button>
          <button
            className="control-btn play-pause"
            onClick={handlePlayPause}
            disabled={playbackState === 'completed'}
            title={playbackState === 'playing' ? 'Pause' : 'Play'}
          >
            {playbackState === 'playing' ? '⏸' : '▶'}
          </button>
          <button
            className="control-btn"
            onClick={handleStepForward}
            disabled={playbackState === 'playing' || progress.current >= progress.total - 1}
            title="Step Forward"
          >
            ⏭
          </button>
        </div>

        <div className="speed-controls-compact">
          {speeds.map(s => (
            <button
              key={s}
              className={`speed-btn ${speed === s ? 'active' : ''}`}
              onClick={() => handleSpeedChange(s)}
              title={`${s}x speed`}
            >
              {s}x
            </button>
          ))}
        </div>
      </div>

      <div className="progress-bar-wrapper">
        <div className="progress-info">
          <span className="progress-text">{progress.percentage.toFixed(0)}%</span>
          <span className="progress-text">{progress.current.toLocaleString()} / {progress.total.toLocaleString()}</span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${progress.percentage}%` }}
          />
          <input
            type="range"
            min="0"
            max={progress.total - 1}
            value={progress.current}
            onChange={handleSeek}
            onMouseMove={handleSliderHover}
            onMouseLeave={handleSliderLeave}
            className="progress-slider"
          />
        </div>
        {hoverTime && tooltipPosition && (
          <div
            className="seekbar-tooltip"
            style={{
              left: `${tooltipPosition.x}px`,
              bottom: '35px'
            }}
          >
            <div className="tooltip-time">{formatDate(hoverTime)} {formatTime(hoverTime)}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BacktestControls;
